import clsx from 'clsx';
import React, { useEffect, useMemo } from 'react';

import useImageAspectRatio from '@hooks/useImageAspectRatio';
import useServerSideRendering from '@hooks/useServerSideRendering';
import { AuthoredContentReactToHtmlParser } from '@utils/reactHtmlParserTransform';

import { FALLBACK_IMAGE_CLASS, FALLBACK_IMAGE_LOADED_CLASS } from './constants';

interface FlourishWrapperProps {
  fallbackImageURL: string;
  iframe: string;
}

const FLOURISH_SCRIPT_URL = 'https://public.flourish.studio/resources/embed.js';

const FlourishWrapper = ({ fallbackImageURL, iframe }: FlourishWrapperProps) => {
  const { aspectRatio, imgRef, onLoad } = useImageAspectRatio();
  const hydrated = useServerSideRendering();

  const aspectRatioStyle = useMemo(() => {
    return aspectRatio ? { aspectRatio } : undefined;
  }, [aspectRatio]);

  const fallbackImage = useMemo(() => {
    return (
      <img
        className="h-auto w-full"
        onLoad={onLoad}
        ref={imgRef}
        src={fallbackImageURL}
        style={aspectRatioStyle}
      />
    );
  }, [fallbackImageURL, aspectRatioStyle, imgRef, onLoad]);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = FLOURISH_SCRIPT_URL;
    script.async = true;

    window.setTimeout(() => {
      document.body.appendChild(script);
      const fallbackImage = document.querySelector(`.${FALLBACK_IMAGE_CLASS}`);
      fallbackImage?.classList.add(FALLBACK_IMAGE_LOADED_CLASS);
    }, 100);
  }, []);

  return !hydrated ? (
    <>{fallbackImage}</>
  ) : (
    <figure className="relative z-10 m-0 h-auto w-full *:bg-white" style={aspectRatioStyle}>
      <div className={clsx(FALLBACK_IMAGE_CLASS, 'absolute -z-10')}>{fallbackImage}</div>
      {AuthoredContentReactToHtmlParser(iframe)}
      <noscript>{fallbackImage}</noscript>
    </figure>
  );
};

export default FlourishWrapper;
